import { lazy, Suspense, useEffect, useState } from 'react';
import Seo from '@/components/Seo';
import { buildLocalBusinessSchema } from '@/lib/seo-schemas';
import Hero from '@/components/sections/Hero';
import TrustBar from '@/components/TrustBar';
import { afterFirstPaint } from '@/api/base44Lazy';

// Speed: the first screen (hero + trust bar) is built and painted straight away. The eight sections
// below the fold come from their own file and are built just after that first paint. On a mid-range
// phone, building all ten sections at once froze the page for ~0.5 s before anything showed.
const loadBelowFold = () => import('@/components/sections/HomeBelowFold');
const HomeBelowFold = lazy(loadBelowFold);

function useAfterFirstPaint() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Only fetch and build the lower sections once the first screen has actually painted,
    // so they can never compete with it.
    let cancelled = false;
    afterFirstPaint(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);
  return ready;
}

// Reserves space so nothing below jumps while the lower sections load.
const BelowFoldPlaceholder = () => <div className="min-h-screen" aria-hidden="true" />;

export default function Home() {
  const localBusinessSchema = buildLocalBusinessSchema();
  const showBelowFold = useAfterFirstPaint();
  return (
    <>
      <Seo
        title="Daley Inventories | Property Inventory, EICR, Gas Safety & EPC in London & Manchester"
        description="Trusted by 20,000+ landlords. Professional inventory reports, EICR, gas safety certificates, EPC, and end-of-tenancy cleaning across London & Manchester. AIIC accredited. Book online today."
        jsonLd={localBusinessSchema}
      />
      <Hero />
      <TrustBar />
      {showBelowFold ? (
        <Suspense fallback={<BelowFoldPlaceholder />}>
          <HomeBelowFold />
        </Suspense>
      ) : (
        <BelowFoldPlaceholder />
      )}
    </>
  );
}
